import { memo, useCallback, useState } from 'react'

import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { MediaPlatformInsert } from '@nativewaves/nw-cloud-sdk/media'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useMediaPlatformCreateMutation } from 'hooks/mutations/media'
import { mediaPlatformsRoute } from 'pages/NWPlatform/MediaPlatforms'
import { workspaceQueryKeys } from 'services/queryKeys'

type FormStructure = MediaPlatformInsert

const NewDialogRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()

  const [submitDisabled, setSubmitDisabled] = useState(false)

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { environmentId } = mediaPlatformsRoute.useParams()

  const { mutate: createMediaPlatform, isSuccess, isError } = useMediaPlatformCreateMutation()

  const { data: workspaces, ...workspacesQuery } = useInfiniteQuery({
    ...workspaceQueryKeys.list({ environmentId }),
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: { environmentId },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setSubmitDisabled(true)
      setCurrentStep({ value: 10, description: `${t('domain:MediaPlatform.creating')} ${data.name}` })

      createMediaPlatform(
        { mediaPlatformInsert: data },
        {
          onSuccess: (data) => {
            setCurrentStep({ value: 100, description: `${data.name} ${t('domain:MediaPlatform.created')}` })
            setTimeout(() => {
              handleClose()
            }, 700)
          },
          onError: (_, variables) => {
            setCurrentStep({
              value: 100,
              description: `${variables.mediaPlatformInsert.name} ${t('domain:MediaPlatform.couldNotBeCreated')}`,
            })
          },
          onSettled: () => {
            setSubmitDisabled(false)
          },
        },
      )
    },
    [setCurrentStep, t, createMediaPlatform, handleClose],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:MediaPlatform.addMediaPlatform')}
      description={t('domain:MediaPlatform.entityCreateDescription')}
      SubmitProgressZone={<SubmissionProgress isSuccess={isSuccess} isError={isError} />}
      SaveButtonProps={{
        disabled: workspacesQuery.isLoading || submitDisabled,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoFocus
              margin="dense"
              label={t('name')}
              type="text"
              fullWidth
              variant="standard"
              required
              autoComplete="off"
            />
          )}
        />

        <Controller
          control={control}
          name="defaultWorkspaceId"
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }} required={!!workspaces} disabled={!workspaces} variant="outlined">
              <InputLabel id="defaultWorkspaceId">
                {workspacesQuery.isLoading
                  ? `${t('domain:MediaPlatform.workspacesLoading')}...`
                  : !workspaces
                    ? t('domain:MediaPlatform.noWorkspaces')
                    : t('domain:MediaPlatform.defaultWorkspace')}
              </InputLabel>
              <Select {...field} labelId="defaultWorkspaceId" label={t('domain:MediaPlatform.defaultWorkspace')}>
                {workspaces?.flat.map((workspace) => (
                  <MenuItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
